import { Button, Modal } from '@safetyhub/ui';
import { OWNER_LABEL } from '../../../../lib/organizations';
import { MembersModals, MembersHandlers, MembersData } from '../types';
import { ExclamationIcon } from './Icons';
import { InfoIcon } from '../../../../components/icons';
import { MemberDetailModal } from './MemberDetailModal';
import { memberDisplayName } from '../../../../lib/members';

interface ModalsProps {
  modals: MembersModals;
  handlers: MembersHandlers;
  data: MembersData;
  isMobile?: boolean;
}

/** 초대 폼 id — 제출 버튼이 base 푸터(폼 밖)에 있어 `form` 속성으로 잇는다. */
const MEMBER_INVITE_FORM_ID = 'member-invite-form';

/**
 * 이 파일의 모달 넷은 모두 **처리 중(`isInviting`·`isChangingRole`·…)에는 닫는 길 전부를 막는다**
 * — 오버레이·Esc·✕ + `closeLocked`. 종전 raw 오버레이는 버튼만 disabled이고 오버레이 클릭은
 * 처리 중에도 닫았다. 폭은 종전 그대로 모바일 `sm` · 데스크톱 `md`다.
 */
function lockWhile(busy: boolean) {
  return {
    closeOnOverlay: !busy,
    closeOnEsc: !busy,
    showCloseButton: !busy,
    closeLocked: busy,
  };
}

export function InviteModal({ modals, handlers, data, isMobile }: ModalsProps) {
  if (!modals.showInviteModal) return null;

  return (
    <Modal
      open
      onClose={handlers.resetInviteForm}
      title="멤버 초대"
      size={isMobile ? 'sm' : 'md'}
      {...lockWhile(modals.isInviting)}
      footer={
        <>
          <Button
            type="button"
            size={isMobile ? 'sm' : 'md'}
            onClick={handlers.resetInviteForm}
            disabled={modals.isInviting}
          >
            닫기
          </Button>
          {/* 푸터는 base가 본문과 **형제**로 그리므로 `<form>` 밖이다 — `form` 속성으로 제출을 잇는다
              (web 어댑터 `components/Modal.tsx`의 footer docblock). */}
          <Button
            type="submit"
            form={MEMBER_INVITE_FORM_ID}
            variant="primary"
            size={isMobile ? 'sm' : 'md'}
            disabled={modals.isInviting || (modals.inviteType === 'identifier' ? !modals.inviteIdentifier.trim() : !modals.invitePhone.trim())}
          >
            {modals.isInviting ? '초대 중...' : '초대'}
          </Button>
        </>
      }
    >
      {/* 안내 박스 정본은 brand(Billing 리스킨과 같은 처방) — 파랑은 알림 타입 색 체계라 여기 아니다 */}
      <div className={`flex items-start gap-2 bg-brand-50 border border-brand-200 rounded-card px-3 py-2 mb-4 ${isMobile ? 'text-xs' : 'text-sm'}`}>
        <InfoIcon className="flex-shrink-0 mt-0.5 h-4 w-4 text-brand-500" />
        <p className="text-brand-700">
          기존 사용자는 이메일/아이디로, 미가입자는 전화번호로 초대합니다.
        </p>
      </div>

      <form id={MEMBER_INVITE_FORM_ID} onSubmit={(e) => { e.preventDefault(); handlers.handleInvite(); }} className="space-y-4">
        {/* 초대 방식 선택 */}
        <div>
          <label className={`block font-medium text-ink-700 mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            초대 방식
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="identifier"
                checked={modals.inviteType === 'identifier'}
                onChange={() => handlers.setInviteType('identifier')}
                className="form-radio h-4 w-4 text-brand-600"
              />
              <span className={`ml-2 text-ink-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>이메일/아이디</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="phone"
                checked={modals.inviteType === 'phone'}
                onChange={() => handlers.setInviteType('phone')}
                className="form-radio h-4 w-4 text-brand-600"
              />
              <span className={`ml-2 text-ink-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>전화번호</span>
            </label>
          </div>
        </div>

        {/* 입력 필드 */}
        {modals.inviteType === 'identifier' ? (
          <div>
            <label htmlFor="identifier" className={`block font-medium text-ink-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              이메일 또는 아이디
            </label>
            <input
              type="text"
              id="identifier"
              value={modals.inviteIdentifier}
              onChange={(e) => handlers.setInviteIdentifier(e.target.value)}
              className={`mt-1 block w-full px-3 py-2 border border-line rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 ${isMobile ? 'text-sm' : ''}`}
              placeholder="user@example.com 또는 user123"
            />
            <p className={`mt-1 text-ink-500 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>@ 포함 시 이메일, 아니면 아이디로 처리됩니다.</p>
          </div>
        ) : (
          <div>
            <label htmlFor="phone" className={`block font-medium text-ink-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              전화번호
            </label>
            <input
              type="tel"
              inputMode="numeric"
              id="phone"
              value={modals.invitePhone}
              onChange={(e) => handlers.setInvitePhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 11))}
              className={`mt-1 block w-full px-3 py-2 border border-line rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 ${isMobile ? 'text-sm' : ''}`}
              placeholder="01012345678"
              maxLength={11}
            />
            <p className={`mt-1 text-ink-500 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>미가입자에게 SMS로 초대 링크가 발송됩니다.</p>
          </div>
        )}

        {/* 역할 — 「소유자로 초대」 토글(payload roleKeys: [owner]) + 조직 역할 멀티셀렉트(#56, roleKeys) */}
        <fieldset>
          <legend className={`block font-medium text-ink-700 mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>역할</legend>
          {/* ⚠️ 「소유자로 초대」는 **소유자 성질**이다(P7-g A) — 서버 `inviteMember`가 `includesOwner`일 때
              초대자의 `isOwner`를 요구하고 조정 가능 키(`member.manage`)로는 열리지 않는다
              (403 `ORGANIZATION_INVITE_OWNER_FORBIDDEN`, 「소유자 지정은 조직 소유자만 할 수 있습니다」).
              그래서 `member.invite`만 가진 사람에게 이 토글을 보이면 눌리는데 거부된다. */}
          {data.isOwner && (
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                id="invite-as-owner"
                checked={modals.inviteAsOwner}
                onChange={(e) => handlers.setInviteAsOwner(e.target.checked)}
                className="h-4 w-4 rounded border-line text-brand-600"
              />
              <span className={`ml-2 text-ink-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                {OWNER_LABEL}로 초대 (모든 권한)
              </span>
            </label>
          )}
          {!modals.inviteAsOwner && (
            <div className="mt-2">
              {modals.isLoadingInviteRoles ? (
                <p className={`text-ink-500 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>역할 목록을 불러오는 중...</p>
              ) : modals.inviteRoleOptions.length === 0 ? (
                <p className={`text-ink-500 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                  일반 멤버로 초대됩니다. 역할은 조직 관리 &gt; 역할·선임에서 나중에 배정할 수 있습니다.
                </p>
              ) : (
                <ul className="max-h-40 overflow-y-auto space-y-1 rounded-control border border-line p-2" aria-label="초대 역할">
                  {modals.inviteRoleOptions.map((role) => (
                    <li key={role.key}>
                      <label className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={modals.inviteRoleKeys.includes(role.key)}
                          onChange={() => handlers.toggleInviteRoleKey(role.key)}
                          className="mt-0.5 h-4 w-4 rounded border-line text-brand-600"
                        />
                        <span className={`text-ink-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {role.name}
                          {role.description && (
                            <span className={`block text-ink-500 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>{role.description}</span>
                          )}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </fieldset>
      </form>
    </Modal>
  );
}

export function RoleChangeModal({ modals, handlers, isMobile }: ModalsProps) {
  if (!modals.roleChangeTarget) return null;

  return (
    <Modal
      open
      onClose={() => handlers.setRoleChangeTarget(null)}
      title={modals.newIsOwner ? `${OWNER_LABEL} 지정` : `${OWNER_LABEL} 해제`}
      size={isMobile ? 'sm' : 'md'}
      {...lockWhile(modals.isChangingRole)}
      footer={
        <>
          <Button
            type="button"
            size={isMobile ? 'sm' : 'md'}
            onClick={() => handlers.setRoleChangeTarget(null)}
            disabled={modals.isChangingRole}
          >
            취소
          </Button>
          <Button
            type="button"
            variant="primary"
            size={isMobile ? 'sm' : 'md'}
            onClick={handlers.handleRoleChange}
            disabled={modals.isChangingRole}
          >
            {modals.isChangingRole ? '변경 중...' : modals.newIsOwner ? '지정' : '해제'}
          </Button>
        </>
      }
    >
      <p className={`text-ink-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
        <strong>{memberDisplayName(modals.roleChangeTarget)}</strong>님을{' '}
        {modals.newIsOwner
          ? `${OWNER_LABEL}로 지정합니다. 조직의 모든 권한을 갖게 됩니다.`
          : `${OWNER_LABEL}에서 해제합니다. 다른 역할(안전관리자 등)은 역할·선임 화면에서 배정할 수 있습니다.`}
      </p>
    </Modal>
  );
}

export function RemoveModal({ modals, handlers, isMobile }: ModalsProps) {
  if (!modals.removeTarget) return null;

  return (
    <Modal
      open
      onClose={() => handlers.setRemoveTarget(null)}
      title="멤버 제거"
      size={isMobile ? 'sm' : 'md'}
      {...lockWhile(modals.isRemoving)}
      footer={
        <>
          <Button
            type="button"
            size={isMobile ? 'sm' : 'md'}
            onClick={() => handlers.setRemoveTarget(null)}
            disabled={modals.isRemoving}
          >
            취소
          </Button>
          <Button
            type="button"
            variant="danger"
            size={isMobile ? 'sm' : 'md'}
            onClick={handlers.handleRemove}
            disabled={modals.isRemoving}
          >
            {modals.isRemoving ? '제거 중...' : '제거'}
          </Button>
        </>
      }
    >
      <div className="flex items-start">
        <div className={`flex-shrink-0 flex items-center justify-center rounded-full bg-red-100 ${isMobile ? 'h-10 w-10' : 'h-12 w-12'}`}>
          <ExclamationIcon className={`text-red-600 ${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
        </div>
        <p className={`ml-4 text-ink-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          <strong>{memberDisplayName(modals.removeTarget)}</strong>님을 조직에서 제거하시겠습니까?
          <br />
          제거된 멤버는 더 이상 조직의 데이터에 접근할 수 없습니다.
        </p>
      </div>
    </Modal>
  );
}

export function CancelInvitationModal({ modals, handlers, isMobile }: ModalsProps) {
  if (!modals.cancelTarget) return null;

  return (
    <Modal
      open
      onClose={() => handlers.setCancelTarget(null)}
      title="초대 취소"
      size={isMobile ? 'sm' : 'md'}
      {...lockWhile(modals.isCancelling)}
      footer={
        <>
          <Button
            type="button"
            size={isMobile ? 'sm' : 'md'}
            onClick={() => handlers.setCancelTarget(null)}
            disabled={modals.isCancelling}
          >
            닫기
          </Button>
          <Button
            type="button"
            variant="danger"
            size={isMobile ? 'sm' : 'md'}
            onClick={handlers.handleCancelInvitation}
            disabled={modals.isCancelling}
          >
            {modals.isCancelling ? '취소 중...' : '초대 취소'}
          </Button>
        </>
      }
    >
      <div className="flex items-start">
        <div className={`flex-shrink-0 flex items-center justify-center rounded-full bg-yellow-100 ${isMobile ? 'h-10 w-10' : 'h-12 w-12'}`}>
          <ExclamationIcon className={`text-yellow-600 ${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
        </div>
        <p className={`ml-4 text-ink-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          <strong>{modals.cancelTarget.email || modals.cancelTarget.phone || (modals.cancelTarget.username && `@${modals.cancelTarget.username}`)}</strong>에 대한 초대를 취소하시겠습니까?
          <br />
          취소된 초대 링크는 더 이상 사용할 수 없습니다.
        </p>
      </div>
    </Modal>
  );
}

export function AllModals(props: ModalsProps) {
  return (
    <>
      <InviteModal {...props} />
      <RoleChangeModal {...props} />
      <RemoveModal {...props} />
      <CancelInvitationModal {...props} />
      <MemberDetailModal modals={props.modals} handlers={props.handlers} data={props.data} isMobile={props.isMobile} />
    </>
  );
}
